import { useEffect, useMemo, useRef, useState } from 'react';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import Drawer from '../components/Drawer';
import Spinner from '../components/Spinner';
import { useUnitMap } from '../hooks/useUnitMap';
import {
  MIXED_DISTRICT_COLOR,
  districtColorMap,
  groupUnitMapUnits,
  markerColor,
  unitTypeLabel,
  type UnitMapUnit,
} from '../lib/unitMap';

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

function markerScale(count: number): number {
  return Math.min(28, 10 + Math.sqrt(Math.max(1, count)) * 4);
}

function membershipLabel(unit: UnitMapUnit): string {
  if (unit.paidYouth === null) return 'Unknown';
  const source = unit.membershipSource === 'recharter' ? 'Recharter' : 'Charter organization report';
  return `${unit.paidYouth} · ${source}${unit.membershipDate ? ` · ${unit.membershipDate}` : ''}`;
}

function addressLabel(unit: UnitMapUnit): string {
  return [unit.address, unit.cityState, unit.zip].filter(Boolean).join(', ') || 'Unknown';
}

function UnitDetails({ unit }: { unit: UnitMapUnit }) {
  return (
    <article className="unit-map-detail">
      <h4>{unit.name}</h4>
      <dl>
        <div><dt>Type</dt><dd>{unitTypeLabel(unit.type)}</dd></div>
        <div><dt>District</dt><dd>{unit.districtName}</dd></div>
        <div><dt>Charter organization</dt><dd>{unit.charterOrg ?? 'Unknown'}</dd></div>
        <div><dt>Charter organization address</dt><dd>{addressLabel(unit)}</dd></div>
        <div><dt>Paid youth</dt><dd>{membershipLabel(unit)}</dd></div>
      </dl>
    </article>
  );
}

export default function UnitMap() {
  const query = useUnitMap();
  const [districtFilter, setDistrictFilter] = useState('All');
  const [selectedZip, setSelectedZip] = useState<string | null>(null);
  const [mapsReady, setMapsReady] = useState(false);
  const [mapsError, setMapsError] = useState('');
  const [mapElement, setMapElement] = useState<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const infoRef = useRef<google.maps.InfoWindow | null>(null);

  const districts = query.data?.districts ?? [];
  const colors = useMemo(() => districtColorMap(districts.map((district) => district.id)), [districts]);
  const allUnits = query.data?.units ?? [];
  const filteredUnits = useMemo(() => districtFilter === 'All'
    ? allUnits
    : allUnits.filter((unit) => unit.districtId === districtFilter), [allUnits, districtFilter]);
  const groups = useMemo(() => groupUnitMapUnits(filteredUnits), [filteredUnits]);
  const mappedCount = groups.reduce((sum, group) => sum + group.units.length, 0);
  const selectedGroup = groups.find((group) => group.zip === selectedZip) ?? null;

  useEffect(() => {
    setDistrictFilter('All');
    setSelectedZip(null);
  }, [query.data?.scope.kind, query.data?.scope.districtId, query.data?.units]);

  useEffect(() => {
    if (!API_KEY || !mapElement || mapRef.current) return;
    let cancelled = false;
    setOptions({ key: API_KEY, v: 'weekly' });
    Promise.all([importLibrary('maps'), importLibrary('core')]).then(([maps, core]) => {
      if (cancelled) return;
      mapRef.current = new maps.Map(mapElement, {
        center: { lat: 39.5, lng: -98.35 }, zoom: 4,
        streetViewControl: false, mapTypeControl: false, fullscreenControl: true,
      });
      infoRef.current = new maps.InfoWindow({ disableAutoPan: true });
      void core;
      setMapsReady(true);
    }).catch((cause) => {
      if (!cancelled) setMapsError(cause instanceof Error ? cause.message : 'Google Maps failed to load.');
    });
    return () => {
      cancelled = true;
      for (const marker of markersRef.current) marker.setMap(null);
      markersRef.current = [];
      infoRef.current?.close();
      infoRef.current = null;
      mapRef.current = null;
    };
  }, [mapElement]);

  useEffect(() => {
    if (!mapsReady || !mapRef.current) return;
    for (const marker of markersRef.current) marker.setMap(null);
    markersRef.current = [];
    infoRef.current?.close();
    if (!groups.length) return;
    const bounds = new google.maps.LatLngBounds();
    for (const group of groups) {
      const position = { lat: group.latitude, lng: group.longitude };
      bounds.extend(position);
      const marker = new google.maps.Marker({
        map: mapRef.current,
        position,
        title: `${group.zip}: ${group.units.length} unit${group.units.length === 1 ? '' : 's'}`,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: markerScale(group.units.length),
          fillColor: markerColor(group, colors), fillOpacity: 0.86,
          strokeColor: '#ffffff', strokeOpacity: 1, strokeWeight: 2,
        },
        label: { text: String(group.units.length), color: '#ffffff', fontWeight: '700', fontSize: '12px' },
        zIndex: group.units.length,
      });
      marker.addListener('mouseover', () => {
        const district = group.districtIds.length === 1 ? group.units[0].districtName : 'Mixed districts';
        const root = document.createElement('div');
        root.className = 'unit-map-hover';
        const heading = document.createElement('strong');
        heading.textContent = `ZIP ${group.zip} · ${group.units.length} unit${group.units.length === 1 ? '' : 's'}`;
        const detail = document.createElement('div');
        detail.textContent = `${district} · ${group.knownYouth === null ? 'Youth unknown' : `${group.knownYouth} known youth`}`;
        root.append(heading, detail);
        infoRef.current?.setContent(root);
        infoRef.current?.open({ map: mapRef.current!, anchor: marker });
      });
      marker.addListener('mouseout', () => infoRef.current?.close());
      marker.addListener('click', () => setSelectedZip(group.zip));
      markersRef.current.push(marker);
    }
    mapRef.current.fitBounds(bounds, 48);
  }, [colors, groups, mapsReady]);

  if (query.loading) return <section className="card"><Spinner label="Loading Unit Map…" size={32} /></section>;
  if (query.error) return <section className="card"><h2>Unit Map</h2><p className="err">{query.error}</p></section>;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Unit Map</h1>
          <p>Units are shown approximately by the ZIP reported for their charter organization.</p>
        </div>
      </div>

      <section className="card unit-map-controls">
        <div className="unit-map-summary">
          <strong>{mappedCount} of {filteredUnits.length} units mapped</strong>
          <span>{groups.length} ZIP location{groups.length === 1 ? '' : 's'} · {filteredUnits.length - mappedCount} unmapped</span>
        </div>
        <label>
          District
          <select value={districtFilter} onChange={(event) => setDistrictFilter(event.target.value)}>
            <option value="All">All authorized districts</option>
            {districts.map((district) => <option key={district.id} value={district.id}>{district.name}</option>)}
          </select>
        </label>
        <div className="unit-map-legend" aria-label="District marker legend">
          {districts.map((district) => (
            <span key={district.id}><i style={{ background: colors.get(district.id) }} />{district.name}</span>
          ))}
          {districts.length > 1 && <span><i style={{ background: MIXED_DISTRICT_COLOR }} />Mixed districts</span>}
        </div>
      </section>

      <section className="card unit-map-card">
        {!API_KEY && <p className="err">Google Maps is not configured.</p>}
        {mapsError && <p className="err">{mapsError}</p>}
        <div ref={setMapElement} className="unit-map-canvas" aria-label="Approximate unit locations by ZIP" />
      </section>
      <p className="unit-map-attribution">Postal coordinates derived from GeoNames data (CC BY 4.0). ZIP placement is approximate.</p>

      {selectedGroup && (
        <Drawer
          title={`ZIP ${selectedGroup.zip}`}
          subtitle={`${selectedGroup.units.length} unit${selectedGroup.units.length === 1 ? '' : 's'}${selectedGroup.knownYouth === null ? ' · Youth unknown' : ` · ${selectedGroup.knownYouth} known youth`}`}
          onClose={() => setSelectedZip(null)}
        >
          {selectedGroup.units.map((unit) => <UnitDetails key={unit.id} unit={unit} />)}
        </Drawer>
      )}
    </>
  );
}
